import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Box, Button, ButtonGroup, TextField, Typography } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import SafeMarkdown from './SafeMarkdown';
import {
  capabilityTextSx,
  editorSurfacePaddingSx,
  markdownBodySx,
  variableTokenChipInteractiveSx,
  variableTokenChipSx,
} from './markdownBodySx';
import { buildVariablePlaceholderRegex } from '../../utils/promptVariables';

const ROW_LINE_HEIGHT_PX = 25.6;
const SURFACE_VERTICAL_PADDING_PX = 24;
const COMPACT_SURFACE_VERTICAL_PADDING_PX = 12;

function computeSurfaceMinHeight(minRows, compact = false) {
  const padding = compact ? COMPACT_SURFACE_VERTICAL_PADDING_PX : SURFACE_VERTICAL_PADDING_PX;
  return minRows * ROW_LINE_HEIGHT_PX + padding;
}

const VARIABLE_SENTINEL_PREFIX = 'pcvar:';

/** Replace {{variables}} with inline-code sentinels so one markdown pass keeps list/block structure. */
function buildVariableSentinelMarkdown(text, variables, variableValues) {
  const source = String(text || '');
  const vars = (variables || []).filter((variable) => variable?.name);
  if (!vars.length) return { markdown: source, sentinels: new Map() };

  const matches = [];
  vars.forEach((variable) => {
    const pattern = buildVariablePlaceholderRegex(variable.name, 'gi');
    let match;
    while ((match = pattern.exec(source)) !== null) {
      matches.push({
        start: match.index,
        end: match.index + match[0].length,
        variable,
      });
    }
  });

  if (!matches.length) return { markdown: source, sentinels: new Map() };

  matches.sort((a, b) => a.start - b.start || a.end - b.end);
  const nonOverlapping = [];
  let lastEnd = -1;
  matches.forEach((entry) => {
    if (entry.start < lastEnd) return;
    nonOverlapping.push(entry);
    lastEnd = entry.end;
  });

  const sentinels = new Map();
  let counter = 0;
  let markdown = source;
  nonOverlapping.sort((a, b) => b.start - a.start);
  nonOverlapping.forEach((entry) => {
    const id = `${VARIABLE_SENTINEL_PREFIX}${counter++}`;
    const raw = variableValues?.[entry.variable.name];
    const hasRaw = raw !== undefined && raw !== null && String(raw).trim() !== '';
    const displayValue = hasRaw
      ? String(raw)
      : (entry.variable.defaultValue || `{{${entry.variable.name}}}`);
    sentinels.set(id, { variable: entry.variable, displayValue });
    markdown = markdown.substring(0, entry.start) + `\`${id}\`` + markdown.substring(entry.end);
  });

  markdown = finalizeVariableSentinelMarkdown(markdown);

  return { markdown, sentinels };
}

/**
 * Keep single newlines before variable sentinels in the same paragraph (hard break).
 * Double newlines stay as separate paragraphs so blank lines in edit survive preview.
 */
function finalizeVariableSentinelMarkdown(markdown) {
  return String(markdown || '').replace(
    /([^\n])\n(`pcvar:\d+`)/g,
    '$1  \n$2'
  );
}

export default function MarkdownPreviewEditor({
  value = '',
  onChange,
  mode = 'edit',
  onModeChange,
  placeholder = '',
  minRows = 4,
  renderEditor,
  previewSx,
  editorSx,
  showToggle = Boolean(onModeChange),
  variables = [],
  variableValues = {},
  onVariableClick,
  clickAwayToPreview = false,
  onBeforePreview,
  keepEditorMounted = false,
  editorControlRef,
  compact = false,
  embedded = false,
  fillContainer = false,
  contentTextSx = capabilityTextSx,
}) {
  const rootRef = useRef(null);
  const isPreview = mode === 'preview';
  const hasValue = String(value || '').trim().length > 0;
  const surfaceMinHeight = computeSurfaceMinHeight(minRows, compact);
  const sharedSurfaceSx = useMemo(
    () => {
      if (fillContainer) {
        return {
          flex: 1,
          minHeight: 0,
          height: '100%',
          width: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
        };
      }
      if (embedded) {
        return {
          minHeight: minRows * ROW_LINE_HEIGHT_PX,
          px: 0,
          py: 0,
          width: '100%',
          boxSizing: 'border-box',
        };
      }
      return {
        minHeight: surfaceMinHeight,
        ...(compact ? { px: 0, py: 0.75 } : editorSurfacePaddingSx),
        boxSizing: 'border-box',
      };
    },
    [fillContainer, embedded, surfaceMinHeight, compact, minRows]
  );
  const rootSx = useMemo(
    () => ({
      width: '100%',
      ...(fillContainer
        ? { flex: 1, minHeight: 0, height: '100%', display: 'flex', flexDirection: 'column' }
        : {}),
    }),
    [fillContainer]
  );

  /** Shared padding inset so edit and preview text align pixel-perfect when fillContainer. */
  const fillInsetSx = useMemo(() => {
    if (!fillContainer) return null;
    const padSource = editorSx || editorSurfacePaddingSx;
    return {
      flex: 1,
      minHeight: 0,
      width: '100%',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      px: padSource?.px ?? editorSurfacePaddingSx.px,
      py: padSource?.py ?? editorSurfacePaddingSx.py,
    };
  }, [fillContainer, editorSx]);

  const previewMarkdownModel = useMemo(
    () => buildVariableSentinelMarkdown(value, variables, variableValues),
    [value, variables, variableValues]
  );
  const hasVariableSentinels = previewMarkdownModel.sentinels.size > 0;

  const previewMarkdownBaseSx = useMemo(
    () => ({
      ...markdownBodySx,
      ...(previewSx?.['& .markdown-body'] || {}),
    }),
    [previewSx]
  );

  const variableMarkdownComponents = useMemo(() => ({
    code: ({ className, children, ...props }) => {
      const raw = String(children).replace(/\n$/, '').trim();
      const isInlineCode = !className;
      if (isInlineCode && previewMarkdownModel.sentinels.has(raw)) {
        const { variable, displayValue } = previewMarkdownModel.sentinels.get(raw);
        return (
          <Box
            component="span"
            onClick={
              onVariableClick
                ? (event) => {
                    event.stopPropagation();
                    onVariableClick(variable);
                  }
                : undefined
            }
            sx={onVariableClick ? variableTokenChipInteractiveSx : variableTokenChipSx}
          >
            {displayValue}
          </Box>
        );
      }
      return (
        <code className={className} {...props}>
          {children}
        </code>
      );
    },
  }), [previewMarkdownModel.sentinels, onVariableClick]);

  const previewMarkdownBodySx = useMemo(
    () => ({
      ...previewMarkdownBaseSx,
      width: '100%',
      m: 0,
      p: 0,
      color: hasValue ? 'inherit' : undefined,
    }),
    [previewMarkdownBaseSx, hasValue]
  );

  const previewBody = hasValue ? (
    hasVariableSentinels ? (
      <Box
        className="markdown-body"
        sx={{
          whiteSpace: 'normal',
          ...previewMarkdownBodySx,
          '& p + p': { mt: '1.6em' },
        }}
      >
        <ReactMarkdown
          skipHtml
          remarkPlugins={[remarkGfm, remarkBreaks]}
          urlTransform={(url) => (/^javascript:/i.test(String(url)) ? '' : url)}
          components={variableMarkdownComponents}
        >
          {previewMarkdownModel.markdown}
        </ReactMarkdown>
      </Box>
    ) : (
      <SafeMarkdown
        markdownBaseSx={previewMarkdownBodySx}
        sx={{ m: 0, p: 0, width: '100%', color: hasValue ? 'inherit' : undefined }}
      >
        {value}
      </SafeMarkdown>
    )
  ) : (
    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', ...contentTextSx, color: 'text.secondary' }}>
      {placeholder}
    </Typography>
  );

  const switchToPreview = useCallback(() => {
    onBeforePreview?.();
    onModeChange?.('preview');
  }, [onBeforePreview, onModeChange]);

  const placeCaretAtPoint = useCallback((editorEl, x, y) => {
    if (!editorEl || x == null || y == null) {
      editorEl?.focus?.();
      return;
    }
    editorEl.focus();
    const doc = document;
    const range = doc.caretRangeFromPoint?.(x, y)
      || (() => {
        const pos = doc.caretPositionFromPoint?.(x, y);
        if (!pos) return null;
        const nextRange = doc.createRange();
        nextRange.setStart(pos.offsetNode, pos.offset);
        nextRange.collapse(true);
        return nextRange;
      })();
    if (!range || !editorEl.contains(range.startContainer)) return;
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  }, []);

  const enterEditMode = useCallback((event) => {
    const point = event?.clientX != null
      ? { x: event.clientX, y: event.clientY }
      : null;
    onModeChange?.('edit');
    if (!keepEditorMounted || !editorControlRef?.current) return;
    requestAnimationFrame(() => {
      const editorEl = editorControlRef.current.getElement?.() || editorControlRef.current;
      if (point) {
        placeCaretAtPoint(editorEl, point.x, point.y);
      } else {
        editorEl?.focus?.();
      }
    });
  }, [editorControlRef, keepEditorMounted, onModeChange, placeCaretAtPoint]);

  useEffect(() => {
    if (!clickAwayToPreview || isPreview || !onModeChange) return undefined;
    const handlePointerDown = (event) => {
      const target = event.target;
      if (!target) return;
      if (rootRef.current?.contains(target)) return;
      if (target.closest?.('[data-markdown-clickaway-ignore="true"]')) return;
      switchToPreview();
    };
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [clickAwayToPreview, isPreview, onModeChange, switchToPreview]);

  const previewLayer = (
    <Box
      onMouseDown={(event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        enterEditMode(event);
      }}
      sx={{
        ...sharedSurfaceSx,
        boxSizing: 'border-box',
        cursor: onModeChange ? 'text' : 'default',
        ...(fillContainer
          ? { overflow: 'hidden' }
          : {
              ...contentTextSx,
              color: hasValue ? (contentTextSx.color || 'text.primary') : 'text.secondary',
              fontStyle: hasValue ? 'normal' : 'italic',
              overflow: 'auto',
            }),
        ...previewSx,
        ...(fillContainer
          ? {}
          : {
              '& .markdown-body': previewMarkdownBodySx,
            }),
      }}
    >
      {fillContainer ? (
        <Box
          sx={{
            ...fillInsetSx,
            ...contentTextSx,
            overflow: 'auto',
            scrollbarGutter: 'stable',
            WebkitOverflowScrolling: 'touch',
            color: hasValue ? (contentTextSx.color || 'text.primary') : 'text.secondary',
            fontStyle: hasValue ? 'normal' : 'italic',
          }}
        >
          {previewBody}
        </Box>
      ) : (
        previewBody
      )}
    </Box>
  );

  const editorLayer = fillContainer && renderEditor ? (
    <Box
      sx={{
        ...sharedSurfaceSx,
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          ...fillInsetSx,
          ...contentTextSx,
          overflow: 'hidden',
        }}
      >
        {renderEditor()}
      </Box>
    </Box>
  ) : (
    <Box
      sx={{
        ...sharedSurfaceSx,
        ...contentTextSx,
        ...editorSx,
        boxSizing: 'border-box',
      }}
    >
      {renderEditor ? renderEditor() : (
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={minRows}
          value={value}
          onChange={(event) => onChange?.(event.target.value)}
          placeholder={placeholder}
          size="small"
          sx={{ '& .MuiOutlinedInput-root': { px: 0, py: 0 }, '& .MuiInputBase-input': contentTextSx }}
        />
      )}
    </Box>
  );

  if (keepEditorMounted) {
    return (
      <Box ref={rootRef} sx={{ ...rootSx, position: 'relative' }}>
        {showToggle && onModeChange && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <ButtonGroup size="small" variant="outlined">
              <Button variant={!isPreview ? 'contained' : 'outlined'} onClick={() => onModeChange('edit')}>
                Edit
              </Button>
              <Button variant={isPreview ? 'contained' : 'outlined'} onClick={switchToPreview}>
                Preview
              </Button>
            </ButtonGroup>
          </Box>
        )}
        <Box
          sx={{
            position: 'relative',
            ...(fillContainer
              ? { flex: 1, minHeight: 0, height: '100%', display: 'flex', flexDirection: 'column' }
              : { minHeight: surfaceMinHeight }),
          }}
        >
          <Box
            sx={{
              ...(fillContainer && {
                flex: 1,
                minHeight: 0,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
              }),
              ...(isPreview && {
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                opacity: 0,
                pointerEvents: 'none',
                zIndex: 0,
                overflow: 'hidden',
              }),
            }}
          >
            {editorLayer}
          </Box>
          {isPreview && (
            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                ...(fillContainer && {
                  flex: 1,
                  minHeight: 0,
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }),
              }}
            >
              {previewLayer}
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box ref={rootRef} sx={rootSx}>
      {showToggle && onModeChange && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <ButtonGroup size="small" variant="outlined">
            <Button variant={!isPreview ? 'contained' : 'outlined'} onClick={() => onModeChange('edit')}>
              Edit
            </Button>
            <Button variant={isPreview ? 'contained' : 'outlined'} onClick={switchToPreview}>
              Preview
            </Button>
          </ButtonGroup>
        </Box>
      )}
      {isPreview ? previewLayer : editorLayer}
    </Box>
  );
}
